import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PHASE_1, PHASE_2, LIKERT, TOTAL_QUESTIONS } from "../data/questions";
import { Eyebrow, Btn } from "../components/Primitives";
import { api } from "../lib/api";
import logger from "../lib/logger";

// All 26 questions in order. Phase-1 first (Likert), Phase-2 after (multiple-choice).
const ALL_QUESTIONS = [
    ...PHASE_1.map(q => ({ ...q, _phase: 1, type: "likert" })),
    ...PHASE_2.map(q => ({ ...q, _phase: 2, type: "screening" })),
];

const PHASE_2_START_INDEX = PHASE_1.length;

// Module-level constants for Framer Motion props — avoid per-render allocation.
const PROGRESS_TRANSITION = { duration: 0.5, ease: [0.22, 1, 0.36, 1] };
const QUESTION_FADE_INIT = { opacity: 0, y: 16 };
const QUESTION_FADE_ANIM = { opacity: 1, y: 0 };
const QUESTION_FADE_EXIT = { opacity: 0, y: -16 };
const QUESTION_TRANSITION = { duration: 0.45, ease: [0.22, 1, 0.36, 1] };

export default function Onboarding({ user, onComplete, onExit }) {
    const saved = user?.quiz_progress;
    const savedIdx = Number.isInteger(saved?.idx) && saved.idx > 0 && saved.idx < ALL_QUESTIONS.length ? saved.idx : 0;
    const [idx, setIdx] = useState(savedIdx);
    const [answers, setAnswers] = useState(() => (saved?.answers && typeof saved.answers === "object" ? saved.answers : {}));
    const [showInterstitial, setShowInterstitial] = useState(false);
    const [resumed, setResumed] = useState(savedIdx > 0);
    const saveTimer = useRef(null);
    const submitted = useRef(false);

    // Debounced auto-save so closing the tab doesn't lose progress
    useEffect(() => {
        if (!user?.session_id || submitted.current) return;
        if (idx === 0 && Object.keys(answers).length === 0) return;
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            api.saveQuizProgress(user.session_id, answers, idx)
                .catch(err => logger.warn("quiz progress save failed:", err));
        }, 600);
        return () => clearTimeout(saveTimer.current);
    }, [answers, idx, user?.session_id]);

    useEffect(() => {
        if (!resumed) return;
        const t = setTimeout(() => setResumed(false), 5000);
        return () => clearTimeout(t);
    }, [resumed]);

    const q = ALL_QUESTIONS[idx];
    const total = ALL_QUESTIONS.length;
    const progress = (idx / total) * 100;
    const currentAnswer = answers[q.id];

    const setAnswer = (a) => setAnswers(prev => ({ ...prev, [q.id]: a }));

    const advance = () => {
        if (idx === PHASE_2_START_INDEX - 1) {
            // Just answered the last Phase 1 question — show interstitial
            setShowInterstitial(true);
            return;
        }
        if (idx < total - 1) {
            setIdx(idx + 1);
        } else {
            // Stop any pending progress save — the quiz is being submitted now.
            submitted.current = true;
            clearTimeout(saveTimer.current);
            // Build payload in shape backend expects
            onComplete(
                ALL_QUESTIONS.map(qx => {
                    const a = answers[qx.id];
                    if (!a) return null;
                    if (qx.type === "likert") {
                        return { question_id: qx.id, score: a.score };
                    }
                    return { question_id: qx.id, choice: a.choice };
                }).filter(Boolean)
            );
        }
    };

    const back = () => {
        if (showInterstitial) {
            setShowInterstitial(false);
            return;
        }
        if (idx > 0) {
            setIdx(idx - 1);
        } else if (onExit) {
            onExit();
        }
    };

    const continueFromInterstitial = () => {
        setShowInterstitial(false);
        setIdx(PHASE_2_START_INDEX);
    };

    const canAdvance = (() => {
        if (!currentAnswer) return false;
        if (q.type === "likert") return currentAnswer.score != null;
        if (q.type === "screening") return !!currentAnswer.choice;
        return false;
    })();

    // Interstitial: shown between Phase 1 and Phase 2
    if (showInterstitial) {
        return (
            <div className="flex-1 flex flex-col bg-cream" data-testid="phase-interstitial">
                <div className="w-full h-[2px] bg-burgundy/10">
                    <div className="h-full bg-gold" style={{ width: `${(PHASE_2_START_INDEX / total) * 100}%` }} />
                </div>

                <div className="flex-1 flex flex-col justify-center px-7">
                    <Eyebrow className="mb-5">Almost there</Eyebrow>
                    <h2 className="font-display text-4xl sm:text-5xl leading-[1.1] text-burgundy tracking-tight">
                        <span className="italic">One more</span> section.
                    </h2>
                    <p className="mt-4 font-display italic text-lg text-ink-muted leading-relaxed">
                        These last ten questions are a values check. Answer honestly — we use them to keep the community safe and well-matched.
                    </p>
                    <div className="mt-6 editorial-rule" />
                </div>

                <div className="px-6 pb-8 pb-safe space-y-3">
                    <Btn data-testid="interstitial-continue-btn" onClick={continueFromInterstitial}>
                        Continue
                    </Btn>
                    <button
                        data-testid="interstitial-back-btn"
                        onClick={back}
                        className="block w-full text-center text-[11px] tracking-[0.28em] uppercase text-ink-muted"
                    >
                        ← Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="onboarding-screen">
            {/* Progress bar */}
            <div className="w-full h-[2px] bg-burgundy/10">
                <motion.div
                    className="h-full bg-gold"
                    initial={false}
                    animate={{ width: `${progress + (100 / total)}%` }}
                    transition={PROGRESS_TRANSITION}
                />
            </div>

            {/* Header */}
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button
                    data-testid="onboarding-back-btn"
                    onClick={back}
                    className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted"
                >
                    ← Back
                </button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">
                    {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} · {Math.round(((idx + 1) / total) * 100)}%
                </span>
            </div>

            {resumed && (
                <div data-testid="quiz-resumed-notice" className="mx-6 mt-2 text-[10px] tracking-[0.28em] uppercase text-gold-deep bg-gold/10 border border-gold/30 px-3 py-2">
                    Welcome back — picking up where you left off
                </div>
            )}

            {/* Question */}
            <div className="flex-1 px-6 sm:px-7 py-6 sm:py-8 flex flex-col">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={q.id}
                        data-testid={`question-${q.id}`}
                        initial={QUESTION_FADE_INIT}
                        animate={QUESTION_FADE_ANIM}
                        exit={QUESTION_FADE_EXIT}
                        transition={QUESTION_TRANSITION}
                        className="flex-1 flex flex-col"
                    >
                        <Eyebrow className="mb-4">
                            {q.type === "likert" ? "Reflection" : "Values check"}
                        </Eyebrow>
                        <h2 className="font-display text-2xl sm:text-3xl leading-[1.2] text-burgundy tracking-tight">
                            {q.prompt}
                        </h2>

                        <div className="mt-8 flex-1">
                            {q.type === "likert" && (
                                <div className="space-y-2" data-testid="likert-group">
                                    {LIKERT.map((opt) => {
                                        const selected = currentAnswer?.score === opt.value;
                                        return (
                                            <button
                                                key={opt.value}
                                                data-testid={`likert-${opt.value}`}
                                                onClick={() => setAnswer({ score: opt.value })}
                                                className={`w-full text-left px-5 py-3.5 border transition-all flex items-center justify-between ${
                                                    selected
                                                        ? "border-burgundy bg-burgundy/[0.04] text-burgundy"
                                                        : "border-burgundy/15 text-ink hover:border-burgundy/40"
                                                }`}
                                            >
                                                <span className="font-display text-lg">{opt.label}</span>
                                                <span
                                                    className={`w-3 h-3 rounded-full transition-all ${
                                                        selected ? "bg-gold" : "bg-burgundy/15"
                                                    }`}
                                                />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {q.type === "screening" && (
                                <div className="space-y-2" data-testid="screening-group">
                                    {q.options.map((opt) => {
                                        const selected = currentAnswer?.choice === opt.key;
                                        return (
                                            <button
                                                key={opt.key}
                                                data-testid={`screening-${opt.key}`}
                                                onClick={() => setAnswer({ choice: opt.key })}
                                                className={`w-full text-left px-5 py-3.5 border transition-all flex items-start gap-3 ${
                                                    selected
                                                        ? "border-burgundy bg-burgundy/[0.04] text-burgundy"
                                                        : "border-burgundy/15 text-ink hover:border-burgundy/40"
                                                }`}
                                            >
                                                <span className={`mt-0.5 text-[10px] tracking-[0.24em] uppercase font-sans ${selected ? "text-gold-deep" : "text-ink-muted"}`}>
                                                    {opt.key}
                                                </span>
                                                <span className="font-display text-base leading-snug flex-1">
                                                    {opt.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* CTA */}
            <div className="sticky bottom-0 bg-cream/95 backdrop-blur-md px-6 pb-8 pt-3 pb-safe z-20">
                <Btn
                    data-testid="onboarding-continue-btn"
                    onClick={advance}
                    disabled={!canAdvance}
                    className={!canAdvance ? "opacity-40 cursor-not-allowed" : ""}
                >
                    {idx === total - 1 ? "Reveal my archetype" : "Continue"}
                </Btn>
            </div>
        </div>
    );
}
